/* Blanc Space device preference
 *
 * The mobile-menu selector is the explicit source of truth. Until it is used,
 * search, shop-by-phone collections and the product picker may infer a model.
 * A standard cart attribute mirrors the browser preference so Liquid can
 * render the correct collection-card variants on the first response of later
 * pages. The trailing "__" is a legacy key name, not Shopify's private-attribute
 * prefix; truly private attributes cannot be read from Liquid.
 */
(() => {
  if (window.BlancSpaceDevicePreference) return;

  const MODEL_KEY = 'blancspace.phoneModel';
  const SOURCE_KEY = 'blancspace.phoneModelSource';
  const MODEL_ATTRIBUTE = 'blancspace_phone_model__';
  const SOURCE_ATTRIBUTE = 'blancspace_phone_model_source__';
  const VISUAL_MODEL_ATTRIBUTE = 'blancspace_visual_phone_model__';
  const config = window.BlancSpaceDevicePreferenceConfig || {};
  let serverModel = String(config.serverModel || '').trim();
  let serverSource = String(config.serverSource || '').trim();
  let serverVisualModel = String(config.serverVisualModel || '').trim();
  let syncQueue = Promise.resolve(true);

  const userAgent = `${navigator.userAgent || ''} ${navigator.vendor || ''}`;
  let detectedBrand = /SamsungBrowser|SAMSUNG|\bSM-[A-Z0-9]+|\bGT-[A-Z0-9]+/i.test(userAgent)
    ? 'samsung'
    : 'apple';
  let detectedVisualModel = String(
    (detectedBrand === 'samsung' ? config.samsungVisualModel : config.appleVisualModel) || ''
  ).trim();

  const resolveDetectedBrand = async () => {
    if (detectedBrand === 'samsung' || !/Android/i.test(userAgent)) return detectedBrand;

    try {
      const hints = await navigator.userAgentData?.getHighEntropyValues?.(['model', 'platform']);
      const hintedDevice = `${hints?.model || ''} ${hints?.platform || ''}`;
      if (/SAMSUNG|\bSM-[A-Z0-9]+|\bGT-[A-Z0-9]+/i.test(hintedDevice)) detectedBrand = 'samsung';
    } catch (error) {
      // Client hints are optional; ambiguous Android traffic uses Apple imagery.
    }

    detectedVisualModel = String(
      (detectedBrand === 'samsung' ? config.samsungVisualModel : config.appleVisualModel) || ''
    ).trim();
    return detectedBrand;
  };

  const read = (key) => {
    try { return localStorage.getItem(key) || ''; } catch (error) { return ''; }
  };

  const write = (key, value) => {
    try {
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
    } catch (error) {
      // The storefront remains usable when storage is unavailable.
    }
  };

  const getSource = () => read(SOURCE_KEY) || serverSource;
  const get = () => {
    const localSource = read(SOURCE_KEY);
    if (serverSource === 'menu' && localSource !== 'menu') return serverModel;
    return read(MODEL_KEY) || serverModel;
  };
  const isLocked = () => getSource() === 'menu';
  const getDetectedBrand = () => detectedBrand;
  const getVisualModel = () => get() || detectedVisualModel || serverVisualModel;
  const getServerVisualModel = () => serverVisualModel;

  const syncCart = (model = get(), source = getSource()) => {
    const normalizedModel = String(model || '').trim();
    const normalizedSource = normalizedModel ? String(source || 'inferred').trim() : '';
    if (normalizedModel === serverModel && normalizedSource === serverSource) return Promise.resolve(true);

    syncQueue = syncQueue.then(async () => {
      if (normalizedModel === serverModel && normalizedSource === serverSource) return true;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            attributes: {
              [MODEL_ATTRIBUTE]: normalizedModel,
              [SOURCE_ATTRIBUTE]: normalizedSource,
            },
          }),
          credentials: 'same-origin',
          keepalive: true,
        });
        if (!response.ok) throw new Error(`Device preference update failed: ${response.status}`);
        serverModel = normalizedModel;
        serverSource = normalizedSource;
        return true;
      } catch (error) {
        return false;
      }
    });

    return syncQueue;
  };

  const syncVisualCart = (model = detectedVisualModel) => {
    const normalizedModel = String(model || '').trim();
    if (get() || !normalizedModel || normalizedModel === serverVisualModel) return Promise.resolve(true);

    syncQueue = syncQueue.then(async () => {
      if (get() || normalizedModel === serverVisualModel) return true;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [VISUAL_MODEL_ATTRIBUTE]: normalizedModel } }),
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`Visual device context update failed: ${response.status}`);
        serverVisualModel = normalizedModel;
        window.dispatchEvent(new CustomEvent('blancspace:visual-phone-model-change', {
          detail: { model: normalizedModel, brand: detectedBrand },
        }));
        return true;
      } catch (error) {
        return false;
      }
    });

    return syncQueue;
  };

  const set = async (model, { source = 'inferred', force = false, origin = '' } = {}) => {
    const normalizedModel = String(model || '').trim();
    const explicitMenuChoice = source === 'menu';

    if (isLocked() && !explicitMenuChoice && !force) {
      return { changed: false, locked: true, synced: true, model: get() };
    }

    const normalizedSource = normalizedModel ? (explicitMenuChoice ? 'menu' : 'inferred') : '';
    const changed = normalizedModel !== get() || normalizedSource !== getSource();
    write(MODEL_KEY, normalizedModel);
    write(SOURCE_KEY, normalizedSource);
    const synced = await syncCart(normalizedModel, normalizedSource);

    if (changed) {
      window.dispatchEvent(new CustomEvent('blancspace:phone-model-change', {
        detail: {
          model: normalizedModel,
          source: normalizedSource,
          // Distinguishes WHERE a menu-weight pick happened: the header menu
          // navigates away, but the newsletter popup stays on the page and
          // needs in-place rerenders instead of a reload.
          origin,
          locked: normalizedSource === 'menu',
          synced,
        },
      }));
    }

    return { changed, locked: normalizedSource === 'menu', synced, model: normalizedModel };
  };

  let storedModel = read(MODEL_KEY);
  let storedSource = read(SOURCE_KEY);

  // A previous explicit menu choice beats stale browser inference. This also
  // keeps the cart attribute authoritative across tabs and devices restored by
  // the browser's session storage machinery.
  if (serverSource === 'menu' && storedSource !== 'menu') {
    write(MODEL_KEY, serverModel);
    write(SOURCE_KEY, 'menu');
    storedModel = serverModel;
    storedSource = 'menu';
  } else {
    if (!storedModel && serverModel) {
      write(MODEL_KEY, serverModel);
      storedModel = serverModel;
    }
    if (!storedSource && serverSource) {
      write(SOURCE_KEY, serverSource);
      storedSource = serverSource;
    }
  }

  const ready = storedModel && (storedModel !== serverModel || (storedSource || 'inferred') !== serverSource)
    ? syncCart(storedModel, storedSource || 'inferred')
    : Promise.resolve(true);
  const visualReady = storedModel
    ? Promise.resolve(true)
    : resolveDetectedBrand().then(() => syncVisualCart());

  window.BlancSpaceDevicePreference = {
    get,
    getSource,
    isLocked,
    getDetectedBrand,
    getVisualModel,
    getServerVisualModel,
    set,
    sync: syncCart,
    ready,
    visualReady,
  };
})();
